import _ from "lodash";
import React, { useEffect, useState, useContext } from "react";
// react-bootstrap components
import { Card, Container, Row, Col } from "react-bootstrap";
import { apiService } from "../../../service/ApiService";
import { notificationService } from "../../../service/NotificationService";
import { exportToExcel } from "utils/AppUtils";
import CreateTableBtn from "components/Buttons/CreateTableBtn";
import StoreContext from "contexts/StoreContext";
import HippoTable from "components/ReactTable/HipposReactTable";
import StatusColumn from "components/Custom/StatusColumn";
import ColorColumn from "components/Custom/ColorColumn";
import TimestampColumn from "components/Custom/TimestampColumn";
import EditButton from "views/Components/EditButton";
import ShowModalButton from "views/Components/ShowModalButton";
import ExtraCategoriesSelectModal from "components/Modals/ExtraCategoriesSelectModal";
import ProductsOrderModal from "components/Modals/ProductsOrderModal";
import ExportToExcelButton from "views/Components/ExportToExcelButton";
import ImportCsvButton from "views/Components/ImportCsvButton";
import ImportCsvModal from "components/Modals/ImportCsvModal";
import CopyCategoryModal from "components/Modals/CopyCategoryModal";
import moment from "moment";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

function CategoryListPage() {
  const {
    t,
  } = useTranslation();

  const [data, setData] = useState([]);
  const { storeId, setStoreId } = useContext(StoreContext);
  const [alert, setAlert] = useState(null);
  const [show, setShow] = useState(false);
  const [showOrderEdit, setShowOrderEdit] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [copyCategory, setCopyCategory] = useState({ id: null, name: "" });

  const handleClose = () => setShow(false);
  const handleCloseOrderEdit = () => setShowOrderEdit(false);

  const handleShow = (id) => {
    setCategoryId(id);
    setShow(true);
  };

  const handleShowCopy = (category) => {
    setCopyCategory({ id: category.id, name: category.name });
    setShowCopy(true);
  };

  const handleShowOrderEdit = (id) => {
    setCategoryId(id);
    setShowOrderEdit(true);
  };

  const columns = [
    {
      Header: "ID",
      accessor: "id",
      maxWidth: 70,
      minWidth: 40,
      width: 60,
    },
    {
      Header: t("tables.headers.designation"),
      accessor: "name",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.position"),
      accessor: "order",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.fontSizeCompact"),
      accessor: "fontSize",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.parentCategory"),
      accessor: "parent",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.status"),
      accessor: "status",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.color"),
      accessor: "colorComponent",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.actions"),
      accessor: "edit",
      minWidth: 200,
      maxWidth: 200,
      width: 200,
    },
  ];

  const fetchData = async () => {
    try {
      setShowLoader(true);
      const dataResponse = await apiService.get(`api/v1/categories/store/${storeId}`);
      setData(attachCustom(dataResponse));
    } catch (error) {
      console.error("Error fetching data:", error);
      notificationService.notifyWarn(error.message);
    } finally {
      setShowLoader(false);
    }
  };

  const attachCustom = (data) => {
    const enhanced = data.map((el) => {
      el.status = <StatusColumn value={el.deleted} />;
      el.colorComponent = <ColorColumn color={el.color} />;
      el.timestamp = <TimestampColumn timestamp={el.updatedAt} />;
      el.edit = (
        <div>
          <ShowModalButton handler={() => handleShowOrderEdit(el.id)} variant="info" icon="fa fa-list" className="text-info btn-link" title={t("pages.titles.editProductOrder")} />
          <ShowModalButton handler={() => handleShow(el.id)} variant="success" icon="fa fa-plus" className="text-success btn-link" title={t("common.actions.assignExtraCategories")} />
          <ShowModalButton handler={() => handleShowCopy(el)} variant="warning" icon="fa fa-copy" className="text-warning btn-link" title={t("pages.titles.copyCategory")} />
          <EditButton uri={`/admin/category/${el.id}`} />
        </div>
      );
      el.parent = !el.parentId ? "" : data.find((o) => o.id === el.parentId).name;
      return el;
    });
    return _.sortBy(enhanced, ["order", "id"]);
  };

  const formatData = (data) => {
    const formattedData = data.map((category) => {
      return {
        ID: category.id,
        [t("tables.headers.designation")]: category.name,
        [t("tables.headers.parentCategory")]: category.parentId ? data.find((o) => o.id === category.parentId).name : null,
        [t("tables.headers.position")]: category.order,
        [t("tables.headers.updatedAt")]: moment(category.updatedAt).format("DD/MM/YYYY HH:mm:ss"),
        [t("tables.headers.fontSize")]: category.fontSize,
        [t("tables.headers.color")]: category.color,
        [t("tables.headers.deleted")]: category.deleted ? t("common.actions.yes") : t("common.actions.no"),
        [t("tables.headers.deletedAt")]: category.deletedAt ? moment(category.deletedAt).format("DD/MM/YYYY HH:mm:ss") : null,
      };
    });
    return formattedData;
  };

  const handleExportData = async () => {
    try {
      setExcelLoading(true);
      const dataResponse = await apiService.get(`api/v1/categories/store/${storeId}`);
      exportToExcel(formatData(dataResponse), `categories_${moment().format("YYYYMMDD")}`, t("messages.storeProductCategories"));
      setExcelLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      notificationService.notifyWarn(error.message);
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    if (!storeId) return;
    fetchData();
  }, [storeId]);

  return (
    <>
      {alert}
      <ExtraCategoriesSelectModal categoryId={categoryId} show={show} handleClose={handleClose} />
      <ProductsOrderModal categoryId={categoryId} show={showOrderEdit} handleClose={handleCloseOrderEdit} />
      <CopyCategoryModal categoryId={copyCategory.id} categoryName={copyCategory.name} show={showCopy} handleClose={() => setShowCopy(false)} />
      <ImportCsvModal type="categories" title={t("pages.titles.importCategories")} show={showImport} handleClose={() => setShowImport(false)} onImported={fetchData} />
      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <Row className="mx-1">
                  <CreateTableBtn route={"/admin/category"} />
                  <ExportToExcelButton handler={handleExportData} disabled={excelLoading} />
                  <ImportCsvButton handler={() => setShowImport(true)} />
                </Row>
                <Row>
                  <Col>
                    {showLoader ? (
                      <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
                        <ClipLoader size={50} color={"#ff6fbc"} />
                      </div>
                    ) : (
                      <HippoTable data={data} columns={columns} className="-striped -highlight primary-pagination" />
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CategoryListPage;
